import { CommonModule } from '@angular/common'
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    inject,
    input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import { FormControl, ReactiveFormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { Subscription, switchMap } from 'rxjs'
import { SvgIconComponent } from '../svg-icon/svg-icon.component'

type InputType = 'text' | 'password' | 'email' | 'number' | 'tel' | 'url' | 'search'
@Component({
    selector: 'app-outlined-input-field',
    templateUrl: './outlined-input-field.component.html',
    styleUrls: ['./outlined-input-field.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, ReactiveFormsModule, MatInputModule, MatFormFieldModule, MatIconModule, MatButtonModule, SvgIconComponent],
})
export class CustomInputComponent implements OnInit, OnDestroy {
    label = input('')
    placeholder = input('')
    maxLength = input('')
    type = input<InputType>('text')
    required = input(false)
    control = input.required<FormControl>()
    control$ = toObservable(this.control)
    errorMessage = input('')
    startIcon = input('')
    endIcon = input('')

    @Output() valueChange = new EventEmitter<string | number>()
    @Output() endIconClick = new EventEmitter<void>()
    @ViewChild('inputElement') inputElement?: ElementRef

    private cdr = inject(ChangeDetectorRef)

    private subscriptions: Subscription = new Subscription()

    isPasswordVisible: boolean = false

    get inputType(): InputType {
        if (this.type() === 'password' && this.isPasswordVisible) {
            return 'text'
        }
        return this.type()
    }

    get isPassword(): boolean {
        return this.type() === 'password'
    }

    ngOnInit() {
        this.subscriptions.add(
            this.control$.pipe(switchMap((control) => control.events)).subscribe((ev) => {
                this.cdr.detectChanges()
            })
        )
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe()
    }

    onEndIconClick(event: Event): void {
        event.stopPropagation()
        event.preventDefault()
        this.endIconClick.emit()
    }

    onTouched(): void {
        if (this.control().updateOn === 'blur') {
            this.control().markAsTouched()
        }
    }

    focus() {
        if (this.inputElement) {
            this.inputElement.nativeElement.focus()
        }
    }

    togglePasswordVisibility(event: Event): void {
        event.stopPropagation()
        event.preventDefault()
        if (this.isPassword) {
            this.isPasswordVisible = !this.isPasswordVisible
        }
    }

    onBlur() {
        this.onTouched()
    }

    handleInput(event: Event): void {
        const target = event.target
        if (target instanceof HTMLInputElement) {
            const value = target.value
            if (this.control().updateOn === 'submit') {
                this.control().setValue(value)
            }

            if (this.control().value !== value) {
                this.valueChange.emit(value)
            }
        }
    }
}
